import React, { useMemo } from "react";
import { DeepReportDocumentV1, ReportClaim } from "../reportDocumentV1";
import { buildEvidenceNumbers, ReportEvidenceSheet } from "./ReportEvidenceSheet";

const CLAIM_TYPE_LABELS: Record<string, string> = {
  fact: "事実",
  inference: "推論",
  opinion: "見解",
  forecast: "予測",
  rumor: "噂",
  recommendation: "提案",
  knowledge_note: "自社ナレッジ",
};

export function EvidenceBadges({ evidenceIds, evidenceNumbers }: { evidenceIds?: string[]; evidenceNumbers: ReadonlyMap<string, number> }) {
  if (!evidenceIds?.length) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-1">
      {Array.from(new Set(evidenceIds)).map((id) => evidenceNumbers.has(id) ? (
        <a
          key={id}
          href={`#evidence-${id}`}
          className="rounded-full bg-[#F2E7DC] px-2 py-0.5 text-xs font-bold text-[#7C5A4E] hover:bg-[#EADBC8]"
        >
          根拠 {evidenceNumbers.get(id)}
        </a>
      ) : <span key={id} className="rounded-full bg-[#F2E7DC] px-2 py-0.5 text-xs font-bold text-[#7C5A4E]">根拠</span>)}
    </div>
  );
}

function Claim({ claim, evidenceNumbers }: { claim: ReportClaim; evidenceNumbers: ReadonlyMap<string, number> }) {
  const typeLabel = CLAIM_TYPE_LABELS[claim.claim_type] ?? (claim.claim_type || null);
  return (
    <div className="py-2">
      <p className="leading-7 text-[#4A3E39]">
        {typeLabel && (
          <span className="mr-2 inline-block rounded bg-[#EADBC8] px-1.5 py-0.5 text-xs font-bold text-[#7C5A4E]">
            {typeLabel}
          </span>
        )}
        {claim.text}
      </p>
      <EvidenceBadges evidenceNumbers={evidenceNumbers} evidenceIds={claim.evidence_ids} />
    </div>
  );
}

function ModuleValue({ value, evidenceNumbers }: { value: unknown; evidenceNumbers: ReadonlyMap<string, number> }) {
  if (value == null) return null;
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return <span>{String(value)}</span>;
  }
  if (Array.isArray(value)) {
    return (
      <ul className="list-disc pl-5">
        {value.map((item, index) => (
          <li key={index}>
            <ModuleValue value={item} evidenceNumbers={evidenceNumbers} />
          </li>
        ))}
      </ul>
    );
  }
  if (typeof value === "object") {
    return (
      <dl className="grid gap-2">
        {Object.entries(value as Record<string, unknown>).map(([key, item]) => (
          <div key={key}>
            <dt className="font-bold">{key === "evidence_ids" ? "根拠" : key}</dt>
            <dd className="pl-3">
              {key === "evidence_ids" && Array.isArray(item) ? <EvidenceBadges evidenceIds={item.filter((id): id is string => typeof id === "string")} evidenceNumbers={evidenceNumbers} /> : <ModuleValue value={item} evidenceNumbers={evidenceNumbers} />}
            </dd>
          </div>
        ))}
      </dl>
    );
  }
  return null;
}

const MODULE_LABELS: Record<string, string> = {
  decision_implications: "意思決定への示唆",
  next_actions: "次のアクション",
};

function hasModuleContent(value: unknown): boolean {
  if (Array.isArray(value)) return value.length > 0;
  if (value && typeof value === "object") return Object.keys(value as Record<string, unknown>).length > 0;
  return value != null && value !== "";
}

export function ReportDocumentRenderer({ document }: { document: DeepReportDocumentV1 }) {
  const evidenceNumbers = useMemo(() => buildEvidenceNumbers(document.evidence), [document.evidence]);
  const visibleModules = document.modules
    ? Object.entries(document.modules).filter(([, value]) => hasModuleContent(value))
    : [];

  return (
    <article className="print:text-black">
      <header className="border-b border-[#EADBC8] pb-5">
        <p className="text-xs font-bold text-[#A67C52]">
          情報基準日 {new Date(document.information_cutoff_at).toLocaleString("ja-JP")}
        </p>
        <h1 className="mt-2 text-3xl font-black text-[#5D4D46]">{document.title}</h1>
      </header>

      <section className="mt-6">
        <h2 className="text-xl font-black text-[#7C5A4E]">要約</h2>
        <div className="mt-2 divide-y divide-[#F2E7DC]">
          {document.executive_summary.map((claim) => (
            <Claim key={claim.id} claim={claim} evidenceNumbers={evidenceNumbers} />
          ))}
        </div>
      </section>

      {document.sections.map((section) => (
        <section key={section.id} className="mt-8">
          <h2 className="text-xl font-black text-[#7C5A4E]">{section.heading}</h2>
          <div className="mt-2 divide-y divide-[#F2E7DC]">
            {section.content_blocks.map((claim) => (
              <Claim key={claim.id} claim={claim} evidenceNumbers={evidenceNumbers} />
            ))}
          </div>
          {!!section.findings?.length && (
            <div className="mt-3 space-y-2">
              {section.findings.map((finding) => (
                <div key={finding.id} className="rounded-xl border border-[#F2E7DC] bg-[#FFFCFA] p-3">
                  <p className="text-sm font-bold text-[#5D4D46]">{finding.text}</p>
                  <EvidenceBadges evidenceNumbers={evidenceNumbers} evidenceIds={finding.evidence_ids} />
                </div>
              ))}
            </div>
          )}
          {section.uncertainty && (
            <p className="mt-3 rounded-xl bg-[#FFF5E8] p-3 text-sm text-[#7C5A4E]">
              不確実性: {section.uncertainty}
            </p>
          )}
        </section>
      ))}

      {!!document.data_points?.length && (
        <section className="mt-8">
          <h2 className="text-xl font-black text-[#7C5A4E]">主要データ</h2>
          <dl className="mt-3 grid gap-3 sm:grid-cols-2">
            {document.data_points.map((item) => (
              <div key={item.id} className="rounded-xl border border-[#F2E7DC] bg-[#FFFCFA] p-4">
                <dt className="text-sm font-bold text-[#8A746A]">{item.label}</dt>
                <dd className="mt-1 text-lg font-black text-[#5D4D46]">
                  {item.value}
                  {item.unit ? ` ${item.unit}` : ""}
                </dd>
                {item.as_of && (
                  <p className="mt-1 text-xs text-[#8A746A]">基準時点: {item.as_of}</p>
                )}
                <EvidenceBadges evidenceNumbers={evidenceNumbers} evidenceIds={item.evidence_ids} />
              </div>
            ))}
          </dl>
        </section>
      )}

      {!!document.counterviews?.length && (
        <section className="mt-8">
          <h2 className="text-xl font-black text-[#7C5A4E]">異なる見方</h2>
          <div className="mt-3 space-y-3">
            {document.counterviews.map((item) => (
              <div key={item.id} className="rounded-xl border border-[#F2E7DC] bg-[#FFFCFA] p-4">
                <p className="leading-7 text-[#4A3E39]">{item.text}</p>
                <EvidenceBadges evidenceNumbers={evidenceNumbers} evidenceIds={item.evidence_ids} />
              </div>
            ))}
          </div>
        </section>
      )}

      {!!document.open_questions?.length && (
        <section className="mt-8">
          <h2 className="text-xl font-black text-[#7C5A4E]">未解決の問い</h2>
          <ul className="mt-3 space-y-2">
            {document.open_questions.map((item, index) => (
              <li key={item.id || index} className="rounded-xl border border-[#F2E7DC] bg-[#FFFCFA] p-3 text-sm text-[#5D4D46]">
                <p>{item.question}</p>
                <EvidenceBadges evidenceNumbers={evidenceNumbers} evidenceIds={item.evidence_ids} />
              </li>
            ))}
          </ul>
        </section>
      )}

      {!!document.watch_next?.length && (
        <section className="mt-8">
          <h2 className="text-xl font-black text-[#7C5A4E]">今後の注目点</h2>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {document.watch_next.map((item) => (
              <div key={item.id} className="rounded-xl border border-[#F2E7DC] bg-[#FFFCFA] p-4">
                <h3 className="font-bold text-[#5D4D46]">{item.label}</h3>
                <p className="mt-1 text-sm text-[#7C5A4E]">{item.reason}</p>
                <EvidenceBadges evidenceNumbers={evidenceNumbers} evidenceIds={item.evidence_ids} />
              </div>
            ))}
          </div>
        </section>
      )}

      {!!visibleModules.length && (
        <section className="mt-8">
          <h2 className="text-xl font-black text-[#7C5A4E]">補足分析</h2>
          <div className="mt-3 grid gap-4">
            {visibleModules.map(([key, value]) => (
              <section key={key} className="rounded-xl border border-[#EADBC8] p-4">
                <h3 className="font-black text-[#5D4D46]">{MODULE_LABELS[key] ?? key}</h3>
                <div className="mt-2 text-sm leading-6 text-[#4A3E39]">
                  <ModuleValue value={value} evidenceNumbers={evidenceNumbers} />
                </div>
              </section>
            ))}
          </div>
        </section>
      )}

      <section className="mt-10 border-t border-[#EADBC8] pt-6">
        <h2 className="text-xl font-black text-[#7C5A4E]">根拠・出典</h2>
        <div className="mt-3">
          <ReportEvidenceSheet document={document} evidenceNumbers={evidenceNumbers} />
        </div>
      </section>
    </article>
  );
}
